import OneSignal from 'react-onesignal';

import { trackNotificationOpened } from './analyticsEvents';
import { env } from './env';
import { Sentry } from './sentry';

import { FIVE_SECONDS } from '@/constants/time';

let initPromise: Promise<void> | null = null;

/**
 * Whether push is usable, resolved once initialization settles. Never rejects,
 * so a caller can branch on availability without a try/catch.
 *
 * Separate from `initPromise` rather than derived from it because the two are
 * read for different reasons: login and logout need the rejection so they can
 * bail, while an opt-in needs a plain answer. Attaching this chain also means
 * an init failure is reported here instead of escaping as an unhandled
 * rejection with no context attached.
 */
let readyPromise: Promise<boolean> | null = null;

let currentLoginUserId: string | null = null;

const PUSH_UNAVAILABLE_MESSAGE =
  /does not support Web Push|^Notification is not defined$|^OneSignal script failed to load/i;

/**
 * Whether web push may run on localhost. Off by default, so dev/prod are
 * unchanged (unset resolves to `false`). Set
 * `EXPO_PUBLIC_PUSH_NOTIFICATIONS_LOCALHOST=true` in `.env.local` to test
 * locally — the OneSignal Web config must also permit the localhost origin
 * for the permission prompt to appear.
 */
export const ALLOW_PUSH_NOTIFICATION_LOCALHOST =
  env.EXPO_PUBLIC_PUSH_NOTIFICATIONS_LOCALHOST === 'true';

/**
 * Initializes the OneSignal SDK for web push notifications.
 *
 * Fire-and-forget: the returned value is intentionally unused by callers.
 * Subsequent calls while initialization is in progress are no-ops — the
 * existing promise is reused. If initialization fails the promise is reset
 * to `null` so a future call can retry.
 */
export function initOneSignal() {
  if (!ALLOW_PUSH_NOTIFICATION_LOCALHOST && window.location.hostname === 'localhost') {
    /*
     * Warns rather than returning quietly because the symptom is otherwise
     * indistinguishable from a broken button: the opt-in control still
     * renders, and pressing it can do nothing at all with no error anywhere.
     */
    console.warn(
      'Push notifications are off on localhost. Set EXPO_PUBLIC_PUSH_NOTIFICATIONS_LOCALHOST=true in .env.local, and point the OneSignal app id at one whose web config permits a localhost origin.'
    );

    return;
  }

  if (initPromise) {
    return;
  }

  initPromise = OneSignal.init({
    appId: env.EXPO_PUBLIC_ONE_SIGNAL_APP_ID,
    safari_web_id: env.EXPO_PUBLIC_ONE_SIGNAL_WEB_SAFARI_ID,
    allowLocalhostAsSecureOrigin: ALLOW_PUSH_NOTIFICATION_LOCALHOST,
  })
    .then(trackNotificationOpens)
    .catch((error: unknown) => {
      initPromise = null;

      throw error;
    });

  readyPromise = initPromise.then(
    () => true,
    (error: unknown) => {
      /*
       * Push being unavailable (no Web Push in iOS Safari outside a home-screen
       * app, no Notification API, or the SDK script blocked by a content blocker)
       * is an expected answer, not a fault: nothing can be fixed from here, and
       * reporting it buries the failures that are. A breadcrumb keeps the cause
       * on whatever event comes next, so a CDN outage is still traceable.
       */
      if (error instanceof Error && PUSH_UNAVAILABLE_MESSAGE.test(error.message)) {
        Sentry.addBreadcrumb({
          category: 'push-notifications',
          level: 'info',
          message: error.message,
        });
      } else {
        Sentry.captureException(error, {
          tags: { component: 'initOneSignal', source: 'push-notifications' },
          extra: { errorMessage: error instanceof Error ? error.message : String(error) },
        });
      }

      return false;
    }
  );
}

/**
 * Whether push can be asked for at all, waiting on initialization but giving
 * up after a bounded delay.
 *
 * The provider's SDK is fetched from its own CDN at runtime, so an extension,
 * a content blocker, or a restrictive network can stop it loading. When that
 * happens the SDK's own permission call never settles, which would leave an
 * opt-in awaiting a promise that resolves at no point — the caller hangs
 * silently and reports nothing either way.
 *
 * The timeout deliberately covers initialization only, never the permission
 * dialog. A dialog stays open for as long as the person takes to decide, so
 * racing that against a clock would record a failure for someone who was
 * about to accept.
 */
export function waitForPushReady() {
  if (!readyPromise) {
    return Promise.resolve(false);
  }

  let timer: ReturnType<typeof setTimeout> | undefined;

  const gaveUp = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), FIVE_SECONDS);
  });

  return Promise.race([readyPromise, gaveUp]).finally(() => clearTimeout(timer));
}

/**
 * Reports a notification click, matching the native listener so a campaign is measured the
 * same wherever it is opened.
 *
 * Registered after init resolves rather than beside it, because the web SDK has no listener
 * registry until the service worker is ready, unlike native where registration is immediate.
 *
 * The launch URL sits on the click result here and on the notification itself on native, so
 * the two files read different fields to emit the same event. That asymmetry is the provider's,
 * not ours.
 *
 * No recipient id, unlike native, because the web SDK exposes no way to read the external id
 * back. It is less needed here: a web click resolves an already-loaded page rather than
 * launching a killed process, so the session has usually identified by the time this fires.
 * Add it if the provider ever exposes a getter.
 */
function trackNotificationOpens() {
  OneSignal.Notifications.addEventListener('click', (event) => {
    trackNotificationOpened({
      notificationId: event.notification?.notificationId,
      launchUrl: event.result?.url,
    });
  });
}

/**
 * Subscribe to OS push-permission changes; invokes `onChange(granted)`
 * whenever the browser permission flips (e.g. the user accepts the prompt).
 * Returns an unsubscribe function. Lets a screen reflect a grant made
 * elsewhere — like the app-level permission modal — without a reload.
 *
 * `granted` is derived from the `PermissionStatus` that fired the event, not
 * from `getPushNotificationPermission()` — OneSignal's cached permission lags
 * the browser grant by a moment, so re-reading it here would report stale
 * `false` and leave the toggles greyed until the next focus/reload.
 *
 * Web-only; the native counterpart is a no-op (permission changes are picked
 * up on focus).
 */
export function subscribeToPushPermissionChange(onChange: (granted: boolean) => void): () => void {
  if (
    typeof navigator === 'undefined' ||
    typeof Notification === 'undefined' ||
    !navigator.permissions?.query
  ) {
    return () => {};
  }

  let status: PermissionStatus | undefined;

  let cancelled = false;

  let query: Promise<PermissionStatus>;

  try {
    query = navigator.permissions.query({ name: 'notifications' as PermissionName });
  } catch {
    /*
     * Privacy extensions and some in-app browsers wrap `permissions.query` and throw
     * from it synchronously. This runs in an effect, so an escape would replace the
     * whole screen with the error boundary for a listener that only refreshes a toggle.
     */
    return () => {};
  }

  query
    .then((permissionStatus) => {
      if (cancelled) {
        return;
      }

      status = permissionStatus;

      status.onchange = () => onChange(permissionStatus.state === 'granted');
    })
    .catch(() => {});

  return () => {
    cancelled = true;

    if (status) {
      status.onchange = null;
    }
  };
}

/**
 * Returns the current push notification permission state from OneSignal.
 */
export function getPushNotificationPermission() {
  return OneSignal.Notifications.permission;
}

/**
 * Requests push notification permission from the user via OneSignal and
 * returns whether it ended up granted.
 *
 * Reads the browser's `Notification.permission` after the prompt resolves, not
 * `getPushNotificationPermission()` (OneSignal's cached value) — OneSignal lags
 * a fresh grant by a moment, so re-reading its cache here returns stale `false`
 * and makes callers skip their `if (granted)` opt-in work. `Notification` is
 * always present on web (push is unsupported without it); the fallback is just
 * a guard.
 */
export async function requestPushNotificationPermissions() {
  await OneSignal.Notifications.requestPermission();

  return typeof Notification !== 'undefined'
    ? Notification.permission === 'granted'
    : getPushNotificationPermission();
}

/**
 * Associates the given `userId` with the current OneSignal device record.
 *
 * Waits for OneSignal initialization to complete before calling the SDK.
 * Returns early if initialization has not started, if `userId` is empty,
 * or if the user is already logged in with the same `userId`.
 *
 * Init failures and SDK rejections are intentionally swallowed: callers
 * use this fire-and-forget, and the OneSignal SDK rejects ops (sometimes
 * with `undefined`) on transient API failures while self-retrying. Letting
 * those escape would surface as `unhandledrejection` events in Sentry with
 * no actionable context.
 */
export async function oneSignalLogin(userId: string) {
  if (!initPromise || !userId || currentLoginUserId === userId) {
    return;
  }

  try {
    await initPromise;
  } catch {
    return;
  }

  try {
    await OneSignal.login(userId);

    currentLoginUserId = userId;
  } catch {
    // SDK rejection swallowed (see fn-level JSDoc); cache stays unset so a
    // future call with the same userId will retry instead of being deduped.
  }
}

/**
 * Dissociates the current user from the OneSignal device record.
 *
 * Waits for OneSignal initialization to complete before calling the SDK.
 * Returns early if initialization has not started.
 *
 * Errors are swallowed for the same reason as `oneSignalLogin`.
 */
export async function oneSignalLogout() {
  if (!initPromise) {
    return;
  }

  try {
    await initPromise;
  } catch {
    return;
  }

  currentLoginUserId = null;

  await OneSignal.logout().catch(() => {});
}
